import React, { createContext, useReducer, useContext, useCallback, useEffect } from 'react';
import { getUserProfile, updateUserProfile } from '../services/userService';
import { useAuth } from './AuthContext';

const initialState = {
  user: null,
  loading: true,
  error: null
};

const userReducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_USER_START':
    case 'UPDATE_USER_START':
      return {
        ...state,
        loading: true,
        error: null
      };
    case 'FETCH_USER_SUCCESS':
    case 'UPDATE_USER_SUCCESS':
      return {
        ...state,
        user: action.payload,
        loading: false,
        error: null
      };
    case 'FETCH_USER_ERROR':
    case 'UPDATE_USER_ERROR':
      return {
        ...state,
        loading: false,
        error: action.payload
      };
    default:
      return state;
  }
};

export const UserContext = createContext(initialState);

export const UserProvider = ({ children }) => {
  const [state, dispatch] = useReducer(userReducer, initialState);
  const { isAuthenticated, logout: logoutAuth } = useAuth();

  const fetchUserData = useCallback(async () => {
    dispatch({ type: 'FETCH_USER_START' });
    try {
      const userData = await getUserProfile();
      dispatch({ type: 'FETCH_USER_SUCCESS', payload: userData });
      return userData;
    } catch (error) {
      if (
        error.message === 'Access token not found' ||
        error.message === 'Refresh token not found' ||
        error.message === 'Failed to refresh token'
      ) {
        dispatch({ type: 'FETCH_USER_SUCCESS', payload: null });
        return null;
      }

      dispatch({ type: 'FETCH_USER_ERROR', payload: error.message });
      throw error;
    }
  }, []);

  const updateUser = useCallback(async (userData) => {
    dispatch({ type: 'UPDATE_USER_START' });
    try {
      const updatedUser = await updateUserProfile(userData);
      dispatch({ type: 'UPDATE_USER_SUCCESS', payload: updatedUser });
    } catch (error) {
      dispatch({ type: 'UPDATE_USER_ERROR', payload: error.message });
      throw error;
    }
  }, []);

  const logoutUser = useCallback(async () => {
    await logoutAuth();
    dispatch({ type: 'FETCH_USER_SUCCESS', payload: null });
  }, [logoutAuth]);

  useEffect(() => {
    if (isAuthenticated || localStorage.getItem('access_token')) {
      fetchUserData().catch(() => {});
    } else {
      dispatch({ type: 'FETCH_USER_SUCCESS', payload: null });
    }
  }, [fetchUserData, isAuthenticated]);

  const value = {
    ...state,
    fetchUserData,
    refreshUserData: fetchUserData,
    updateUser,
    logoutUser
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUser = () => useContext(UserContext);
